// import { useCandidate } from "../../providers/CandidateProvider";
import TalentTileBadges from "./TalentTileBadges";
import { BetaPill, FitRing, FitVerdict, StatGrid, StatRow } from "./CandidateFitPrimitives";
import React from "react";
import { Info } from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";

const FIT_STATUSES = [
    { minScore: 100, label: 'Perfect Match' },
    { minScore: 97, label: 'Exceptional Fit' },
    { minScore: 90, label: 'Excellent Fit' },
    { minScore: 84, label: 'Strong Fit' },
    { minScore: 75, label: 'Good Fit' },
    { minScore: 65, label: 'Moderate Fit' },
    { minScore: 50, label: 'Fair Fit' },
    { minScore: 35, label: 'Weak Fit' },
    { minScore: 15, label: 'Poor Fit' },
    { minScore: 0, label: 'Not a Fit' }
];

/**
 * How the talent's profile scores against the role's criteria. Available as
 * soon as they surface as a match, unlike Hire Confidence.
 *
 * Staff see two extra rows previewing where this tab is going: the ring will
 * eventually carry the blended Role Match, with Profile Match and AI Resume
 * Score as its parts. Until then the ring stays on Profile Match — the figure
 * the client sees — and the blend sits below it, flagged beta. The tab label
 * flips to "Profile Match" for those users so the ring is named for what it
 * currently shows; that lives in the tiles, which own the tab labels.
 */
const CandidateRoleMatch = ({ candidate, onShowMatchDetails, isActive = true }) => {
    const { auth } = useAuth() || {};
    // const { candidate } = useCandidate();

    const score = candidate.roleMatchScore();
    const verdict = FIT_STATUSES.find(status => score >= status.minScore)?.label;

    // Both rows are staff-only while the blend is being evaluated.
    const canSeeBeta = !!auth?.canManageClients();
    const resumeMatchScore = canSeeBeta ? candidate.resumeMatchScore() : null;
    const blendedScore = canSeeBeta ? candidate.blendedMatchScore?.() : null;
    const hasRows = resumeMatchScore !== null || (blendedScore !== null && blendedScore !== undefined);

    // Light vertical padding on purpose: the panel is height-locked by the tab
    // body and centres its own content, so generous `py` here buys nothing
    // visually and comes straight out of the space the match-details link needs.
    return (
        <div className="relative flex flex-1 flex-col items-stretch justify-start gap-[7px] px-0 py-2 text-center">
            {/* Pool / favourite / past-hire tags sit out to the left, absolutely
                positioned so the ring stays centred in the card — the same
                arrangement the previous tile used. */}
            <TalentTileBadges />

            <FitRing score={candidate.roleMatchScore(false)} isActive={isActive} />

            <FitVerdict>{verdict}</FitVerdict>

            {/* Same rows the Hire Confidence tab uses, so the two read as one
                system. Each is dropped rather than zeroed when its score is
                missing — a blank 0 reads as a bad candidate, not absent data. */}
            {hasRows && (
                <StatGrid>
                    {resumeMatchScore !== null && (
                        <StatRow
                            label="AI Resume Score"
                            value={resumeMatchScore}
                            isActive={isActive}
                        />
                    )}

                    {blendedScore !== null && blendedScore !== undefined && (
                        <StatRow
                            isActive={isActive}
                            value={blendedScore}
                            label={(
                                <span className="inline-flex items-center gap-1.5">
                                    <BetaPill />
                                    Role Match
                                </span>
                            )}
                        />
                    )}
                </StatGrid>
            )}

            {/* The criteria-by-criteria breakdown behind the score. Styled to
                match the Engagement Fit affordance on the other tab so the two
                "there's more here" cues read as the same thing. */}
            {onShowMatchDetails && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onShowMatchDetails();
                    }}
                    className="mt-auto inline-flex items-center gap-1 self-center text-[11.5px] font-semibold text-primary underline decoration-primary/40 underline-offset-[3px] hover:decoration-primary"
                >
                    View match details
                    <Info className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                </button>
            )}
        </div>
    )
}

export default CandidateRoleMatch;